/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Typography,
  Select,
  notification,
  InputNumber,
  Radio,
  Checkbox,
  Table,
} from 'antd';
import { validateComparators } from '../utils/itp';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Plus, X } from 'lucide-react';
import { getPopupContainerBody } from './ShippingManagement/shipmentFieldOptions';
import './ShippingManagement/ShipmentDashBoardPage.css';

const { TextArea } = Input;

const { Text } = Typography;
const { Option } = Select;

const MODAL_SELECT_DD = 'modern-dropdown-popup mc-dpr-modal-select-dd';

const AddNewTest = (props) => {
  const {
    isTestModalVisible,
    setIsTestModalVisible,
    sowStationId,
    testTypesList,
    onSaveTestDetails,
    criteriaList,
    inputValue,
    setInputValue,
    valueObj,
    setValueObj,
    testTypeValue,
    setTestTypeValue,
    criteria,
    setCriteria,
    testValue,
    setTestValue,
    testForm,
    setEditTest,
    editTest,
    editTestId,
    loadingBtn,
    setLoadingBtn,
    onSaveEditTestDetails,
    imagesRequired,
    setImagesRequired,
    stopwatchValueRequired,
    setStopwatchValueRequired,
    comparator,
    setComparator,
    selectedTestData,
    isWriteAccess = false,
  } = props;

  const comparatorValue = ['<', '<=', '>', '>=', '=', 'BETWEEN', 'None'];

  const [typeOfTest, setTypeOfTest] = useState('');
  const [testId, setTestId] = useState('');
  const [minValue, setMinValue] = useState('0');
  const [maxValue, setMaxValue] = useState('0');
  const [mandatory, setMandatory] = useState(true);
  // Additional fields - REMOVED: Form manages these values via 'name' prop
  // No need for state when using Ant Design Form with name attribute
  //
  const [isTableSelected, setIsTableSelected] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [colCount, setColCount] = useState(0);
  const [dataTable, setDataTable] = useState({
    rows: [], // Initially empty
    columns: [],
  });
  const [showExtraFields, setShowExtraFields] = useState(true); // Default to showing extra fields (Single mode)
  const [globalComparator, setGlobalComparator] = useState('<');
  const [form] = Form.useForm();
  // Inspection Authority checkbox states
  const [inspectionAuthority, setInspectionAuthority] = useState({
    CRCE: {
      Inspection: false,
      Monitor: false,
      Hold: false,
      WitnessPoint: false,
      ReviewPoint: false,
    },
    Contractor: {
      Inspection: false,
      Monitor: false,
      Hold: false,
      WitnessPoint: false,
      ReviewPoint: false,
    },
    Company: {
      Inspection: false,
      Monitor: false,
      Hold: false,
      WitnessPoint: false,
      ReviewPoint: false,
    },
  });

  const stationMatchesStoredSow = () =>
    String(sessionStorage.getItem('sowStationId') ?? '') === String(sowStationId);

  const closeTestModalShell = () => {
    sessionStorage.removeItem('sowStationId');
    setIsTestModalVisible(false);
  };

  const resolveTestValueTypeId = (criteriaValue) => {
    if (testTypeValue?.id) {
      return testTypeValue.id;
    }
    const selectedCriteria = criteriaList?.find((u) => u.value === criteriaValue);
    if (!selectedCriteria?.valueType) {
      return null;
    }
    const match = testTypesList?.find((u) => u.valueType === selectedCriteria.valueType);
    return match?.id ?? null;
  };

  // Re-resolve test value type when test types load after criteria is already selected
  useEffect(() => {
    if (!criteria || !criteriaList?.length || !testTypesList?.length) {
      return;
    }
    const selected = criteriaList.find((u) => u.value === criteria);
    if (selected && (selected.id === 1 || selected.id === 4)) {
      const match = testTypesList.find((u) => u.valueType === selected.valueType);
      if (match) {
        setTestTypeValue(match);
      }
    }
  }, [criteria, criteriaList, testTypesList, setTestTypeValue]);

  // Effect to populate inspectionAuthority checkboxes when editing
  useEffect(() => {
    if (editTest && isTestModalVisible && stationMatchesStoredSow()) {
      if (criteria === 'Table') {
        setIsTableSelected(true); // Set this first so fields are visible
        if (selectedTestData.tableData) {
          let parsedTableData = selectedTestData.tableData;

          // If tableData is a JSON string, parse it
          if (typeof selectedTestData.tableData === 'string') {
            try {
              parsedTableData = JSON.parse(selectedTestData.tableData);
            } catch (error) {
              console.error('Error parsing tableData JSON:', error);
              parsedTableData = null;
            }
          }

          if (parsedTableData) {
            // Set mode based on tableData.mode
            let isSingleMode = true; // Default
            if (parsedTableData.mode === 'single') {
              isSingleMode = true;
              setShowExtraFields(true);
            } else if (parsedTableData.mode === 'multiple') {
              isSingleMode = false;
              setShowExtraFields(false);
            } else {
              // Default to Single (per table) if mode field doesn't exist
              isSingleMode = true;
              setShowExtraFields(true);
            }

            // Set form field value for radio button
            testForm.setFieldsValue({ extraFieldsMode: isSingleMode });

            // Set global comparator if in single mode
            if (parsedTableData.mode === 'single' && parsedTableData.globalComparator) {
              setGlobalComparator(parsedTableData.globalComparator);
            }

            // Ensure all rows have the required fields initialized, preserving exact structure
            const updatedTableData = {
              ...parsedTableData,
              rows: parsedTableData.rows.map(row => ({
                name: row.name || '',
                unit: row.unit !== undefined ? row.unit : '',
                values: row.values || Array(parsedTableData.columns.length).fill(''),
                maxValue: row.maxValue !== undefined ? row.maxValue : '',
                minValue: row.minValue !== undefined ? row.minValue : '',
                comparator: row.comparator || '<',
              }))
            };
            setDataTable(updatedTableData);
            setRowCount(updatedTableData.rows.length);
            setColCount(updatedTableData.columns.length);
            // Set form field values so they display in the input fields
            testForm.setFieldsValue({
              rowCount: updatedTableData.rows.length,
              colCount: updatedTableData.columns.length,
            });
          } else {
            // If no tableData exists, reset to allow user to create new table
            resetTableState();
          }
        } else {
          // If no tableData exists, reset to allow user to create new table
          resetTableState();
        }
      } else {
        setIsTableSelected(false);
        resetTableState();
      }
      const timer = setTimeout(() => {
        const formValues = testForm.getFieldsValue();
        if (formValues.inspectionAuthority) {
          // If inspectionAuthority is an object with CRCE, Contractor, Company structure
          if (
            formValues.inspectionAuthority.CRCE ||
            formValues.inspectionAuthority.Contractor ||
            formValues.inspectionAuthority.Company
          ) {
            setInspectionAuthority(formValues.inspectionAuthority);
          } else {
            // If it's a string (old format) or null/undefined, use default structure
            const defaultState = {
              CRCE: {
                Inspection: false,
                Monitor: false,
                Hold: false,
                WitnessPoint: false,
                ReviewPoint: false,
              },
              Contractor: {
                Inspection: false,
                Monitor: false,
                Hold: false,
                WitnessPoint: false,
                ReviewPoint: false,
              },
              Company: {
                Inspection: false,
                Monitor: false,
                Hold: false,
                WitnessPoint: false,
                ReviewPoint: false,
              },
            };
            setInspectionAuthority(defaultState);
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    } else if (!editTest && isTestModalVisible && stationMatchesStoredSow()) {
      // When adding new test, check if Table is selected
      // Don't reset isTableSelected if Table criteria is selected (onChangeCriteria already set it)
      if (criteria !== 'Table') {
        setIsTableSelected(false);
        resetTableState();
      }
      // Only reset inspectionAuthority when modal is first opened, not when criteria changes
      // This prevents resetting inspectionAuthority when user changes criteria
    }
  }, [isTestModalVisible, editTest, selectedTestData, criteria, testForm, sowStationId]);

  // Separate effect to reset inspectionAuthority only when modal is first opened (new test)
  useEffect(() => {
    if (isTestModalVisible && !editTest && stationMatchesStoredSow()) {
      // Reset inspectionAuthority only when modal opens for a new test
      setInspectionAuthority({
        CRCE: {
          Inspection: false,
          Monitor: false,
          Hold: false,
          WitnessPoint: false,
          ReviewPoint: false,
        },
        Contractor: {
          Inspection: false,
          Monitor: false,
          Hold: false,
          WitnessPoint: false,
          ReviewPoint: false,
        },
        Company: {
          Inspection: false,
          Monitor: false,
          Hold: false,
          WitnessPoint: false,
          ReviewPoint: false,
        },
      });
    }
  }, [isTestModalVisible, editTest, sowStationId]);

  // Function to reset table state to default values
  const resetTableState = () => {
    setRowCount(0);
    setColCount(0);
    setDataTable({ rows: [], columns: [] });
    setShowExtraFields(true); // Default to single mode
    testForm.setFieldsValue({
      extraFieldsMode: true,
      rowCount: 0,
      colCount: 0,
    });
  };

  const showModal = () => {
    /* Must run before opening so only this station’s instance renders the modal (many AddNewTest per page). */
    sessionStorage.setItem('sowStationId', String(sowStationId));
    setIsTestModalVisible(true);
    setInputValue(false);
    setEditTest(false);
    setShowExtraFields(true); // Reset to default
    setGlobalComparator('<');
    setMinValue('0');
    setMaxValue('0');
    var editField = {
      mandatory: true,
    };
    testForm.setFieldsValue(editField);
    //setting test mandatory by default
  };

  /** Function to reset fields on cancel */
  const handleCancel = () => {
    testForm.resetFields(); //resetting input fields
    closeTestModalShell();
    setInputValue(false);
    setValueObj('');
    setTestTypeValue('');
    setImagesRequired(false);
    setStopwatchValueRequired(false);
    setComparator('<');
    setColCount(0);
    setRowCount(0);
    // Reset inspection authority checkboxes
    setInspectionAuthority({
      CRCE: {
        Inspection: false,
        Monitor: false,
        Hold: false,
        WitnessPoint: false,
        ReviewPoint: false,
      },
      Contractor: {
        Inspection: false,
        Monitor: false,
        Hold: false,
        WitnessPoint: false,
        ReviewPoint: false,
      },
      Company: {
        Inspection: false,
        Monitor: false,
        Hold: false,
        WitnessPoint: false,
        ReviewPoint: false,
      },
    });
    // Form fields are reset via testForm.resetFields() above
    resetTableState();
    setGlobalComparator('<');
  };

  function validateComparator(comparator) {
    if (!data.testValue) {
      notification.warning({
        message: 'Select Unit to Proceed!',
      });
      return;
    }
    if (!globalComparator) {
      notification.warning({
        message: 'Select Comparator to Proceed!',
      });
      return;
    }
    // Validate global min/max values based on comparator
    if (globalComparator !== 'None') {
      if (
        (globalComparator === '<' || globalComparator === '<=') &&
        (!maxValue || maxValue <= 0)
      ) {
        notification.warn({ message: 'Max Value should be greater than 0' });
        return;
      }
      if (
        (globalComparator === '>' || globalComparator === '>=') &&
        (!minValue || minValue <= 0)
      ) {
        notification.warn({ message: 'Min Value should be greater than 0' });
        return;
      }
      if (
        globalComparator === 'BETWEEN' &&
        (!minValue || minValue < 0)
      ) {
        notification.warn({ message: 'Min Value should be greater than or equal to 0' });
        return;
      }
      if (
        globalComparator === 'BETWEEN' &&
        (!minValue || !maxValue || minValue >= maxValue)
      ) {
        notification.warn({ message: 'Max Value should be greater than Min Value' });
        return;
      }
      if (globalComparator === '=' && (!maxValue || maxValue <= 0)) {
        notification.warn({ message: 'Value should be greater than 0' });
        return;
      }
    }
  }
  /** Function to save (add or edit test) */
  const handleOk = () => {
    testForm
      .validateFields()
      .then((data) => {
        if (data.typeOfTest && data.criteria !== undefined) {
          // Check if the required fields are empty
          if (!data.testId || !data.typeOfTest || !data.criteria) {
            notification.warning({
              message: 'Fill All the required fields to proceed!',
            });
            return;
          }

          var sowStatId = sessionStorage.getItem('sowStationId');
          if (data.criteria === 'Accept/Reject/NA' || data.criteria === 'Text') {
            const testValueTypeId = resolveTestValueTypeId(data.criteria);
            if (!testValueTypeId) {
              notification.warning({
                message:
                  'Unable to resolve test value type. Please wait for data to load and try again.',
              });
              return;
            }
            const obj = {
              testId: data.testId,
              typeOfTest: data.typeOfTest,
              testType: data.criteria, // testType: 'Accept/Reject/NA' or 'Text'
              sowStationId: parseInt(sowStatId),
              testValueTypeId,
              mandatory: data.mandatory,
              imagesRequired: imagesRequired,
              stopwatchValueRequired: stopwatchValueRequired,
              acceptanceCriteria: data.acceptanceCriteria,
              frequency: data.frequency,
              applicableStandardsCode: data.applicableStandardsCode,
              testEquipment: data.testEquipment,
              inspectionAuthority: inspectionAuthority,
            };

            if (editTest) {
              onSaveEditTestDetails(obj, editTestId);
            } else {
              onSaveTestDetails(obj);
            }

            setLoadingBtn(true);
            setTimeout(() => {
              setLoadingBtn(false);
              closeTestModalShell();
              setCriteria('');
              setInputValue(false);
              setTestValue('');
              setImagesRequired(false);
              setStopwatchValueRequired(false);
              setComparator('<'); //resetting input fields (includes all form fields)
              resetTableState();
              setGlobalComparator('<');
              testForm.resetFields(); //resetting input fields
            }, 1000);
          } else if (data.criteria === 'Table') {
            // Validate table has rows and columns
            if (dataTable.rows.length === 0 || dataTable.columns.length === 0) {
              notification.warning({
                message: 'Please add rows and columns to the table!',
              });
              return;
            }

            // For Single (per table) mode, check global unit and comparator
            if (showExtraFields) {
              if (!data.testValue) {
                notification.warning({
                  message: 'Select Unit to Proceed!',
                });
                return;
              }
              if (!validateComparators(data, comparator)) {
                return; // Stop submission if validation fails
              }
            } else {
              // For Multiple (per row) mode, check each row has unit and comparator
              const invalidRows = dataTable.rows.filter(row =>
                !row.unit || !row.comparator
              );
              if (invalidRows.length > 0) {
                notification.warning({
                  message: 'Please select Unit and Comparator for all rows!',
                });
                return;
              }
              // Validate min/max values for each row
              for (const row of dataTable.rows) {
                if (row.comparator !== 'None') {
                  if (
                    (row.comparator === '<' || row.comparator === '<=') &&
                    (!row.maxValue || row.maxValue <= 0)
                  ) {
                    notification.warn({ message: `Max Value should be greater than 0 for ${row.name}` });
                    return;
                  }
                  if (
                    (row.comparator === '>' || row.comparator === '>=') &&
                    (!row.minValue || row.minValue <= 0)
                  ) {
                    notification.warn({ message: `Min Value should be greater than 0 for ${row.name}` });
                    return;
                  }
                  if (
                    row.comparator === 'BETWEEN' &&
                    (!row.minValue || row.minValue < 0)
                  ) {
                    notification.warn({ message: `Min Value should be greater than or equal to 0 for ${row.name}` });
                    return;
                  }
                  if (
                    row.comparator === 'BETWEEN' &&
                    (!row.minValue || !row.maxValue || row.minValue >= row.maxValue)
                  ) {
                    notification.warn({ message: `Max Value should be greater than Min Value for ${row.name}` });
                    return;
                  }
                  if (row.comparator === '=' && (!row.maxValue || row.maxValue <= 0)) {
                    notification.warn({ message: `Value should be greater than 0 for ${row.name}` });
                    return;
                  }
                }
              }
            }
            const obj = {
              testId: data.testId,
              typeOfTest: data.typeOfTest,
              testType: 'Table', // testType: 'Table'
              sowStationId: parseInt(sowStatId),
              testValueTypeId: showExtraFields ? (testTypeValue?.id || null) : null,
              mandatory: data.mandatory,
              comparator: comparator,
              minValue: +data.minValue,
              maxValue: +data.maxValue,
              imagesRequired: imagesRequired,
              acceptanceCriteria: data.acceptanceCriteria,
              frequency: data.frequency,
              applicableStandardsCode: data.applicableStandardsCode,
              testEquipment: data.testEquipment,
              inspectionAuthority: inspectionAuthority,
              stopwatchValueRequired: stopwatchValueRequired,
              tableData: {
                ...dataTable,
                mode: showExtraFields ? 'single' : 'multiple', // Add mode flag
              },
            };
            if (obj.tableData.mode === 'multiple') {
              obj.testValueTypeId = 1;
              obj.comparator = '';
            }

            if (editTest) {
              onSaveEditTestDetails(obj, editTestId);
            } else {
              onSaveTestDetails(obj);
            }

            setLoadingBtn(true);
            setTimeout(() => {
              setLoadingBtn(false);
              closeTestModalShell();
              setCriteria('');
              setInputValue(false);
              setTestValue('');
              setImagesRequired(false);
              setStopwatchValueRequired(false);
              setComparator('<');
              resetTableState();
              setGlobalComparator('<');
              testForm.resetFields(); //resetting input fields
            }, 1000);
          } else if (data.testValue !== undefined) {
            if (!validateComparators(data, comparator)) {
              return; // Stop submission if validation fails
            }
            const obj = {
              testId: data.testId,
              typeOfTest: data.typeOfTest,
              testType: 'Value', // testType: 'Value'
              sowStationId: parseInt(sowStatId),
              testValueTypeId: testTypeValue.id,
              minValue: +data.minValue,
              comparator: comparator,
              maxValue: +data.maxValue,
              mandatory: data.mandatory,
              imagesRequired: imagesRequired,
              stopwatchValueRequired: stopwatchValueRequired,
              acceptanceCriteria: data.acceptanceCriteria,
              frequency: data.frequency,
              applicableStandardsCode: data.applicableStandardsCode,
              testEquipment: data.testEquipment,
              inspectionAuthority: inspectionAuthority,
            };
            if (editTest) {
              if (data.testValue) {
                onSaveEditTestDetails(obj, editTestId);

                setLoadingBtn(true);
                setTimeout(() => {
                  setLoadingBtn(false);
                  closeTestModalShell();
                  setCriteria('');
                  setInputValue(false);
                  setTestValue('');
                  setMaxValue('');
                  setMinValue('');
                  setImagesRequired(false);
                  setStopwatchValueRequired(false);
                  setComparator('<');

                  setInspectionAuthority({
                    CRCE: {
                      Inspection: false,
                      Monitor: false,
                      Hold: false,
                      WitnessPoint: false,
                      ReviewPoint: false,
                    },
                    Contractor: {
                      Inspection: false,
                      Monitor: false,
                      Hold: false,
                      WitnessPoint: false,
                      ReviewPoint: false,
                    },
                    Company: {
                      Inspection: false,
                      Monitor: false,
                      Hold: false,
                      WitnessPoint: false,
                      ReviewPoint: false,
                    },
                  });
                  testForm.resetFields(); //resetting input fields
                }, 1000);
              } else {
                notification.warning({
                  message: 'Select Value Unit to Proceed!',
                });
              }
            } else {
              onSaveTestDetails(obj);

              setLoadingBtn(true);
              setTimeout(() => {
                setLoadingBtn(false);
                closeTestModalShell();
                setCriteria('');
                setInputValue(false);
                setTestValue('');
                setMaxValue('');
                setMinValue('');
                setImagesRequired(false);
                setStopwatchValueRequired(false);
                setComparator('<');
                setInspectionAuthority({
                  CRCE: {
                    Inspection: false,
                    Monitor: false,
                    Hold: false,
                    WitnessPoint: false,
                    ReviewPoint: false,
                  },
                  Contractor: {
                    Inspection: false,
                    Monitor: false,
                    Hold: false,
                    WitnessPoint: false,
                    ReviewPoint: false,
                  },
                  Company: {
                    Inspection: false,
                    Monitor: false,
                    Hold: false,
                    WitnessPoint: false,
                    ReviewPoint: false,
                  },
                });
                testForm.resetFields(); //resetting input fields
              }, 1000);
            }
          } else {
            notification.warning({
              message: 'Select Value Unit to Proceed!',
            });
          }
        } else {
          notification.warning({
            message: 'Fill All the required fields to proceed!',
          });
        }
      })
      .catch((error) => {
        console.error('Validation Error:', error);
      });
  };

  /** Function for on select of Criteria */
  const onChangeCriteria = (value) => {
    setCriteria(value);
    if (criteriaList.length > 0) {
      const selected = criteriaList.find((u) => u.value === value);
      if (selected && selected.id === 1) {
        setValueObj(selected.types);
        const testValueTypeId =
          testTypesList && testTypesList.find((u) => u.valueType === selected.valueType);
        setTestTypeValue(testValueTypeId);
        setInputValue(false);
      } else if (selected && selected.id === 3) {
        // For Table, use the same types as Value criteria (id === 2) for unit selection
        const valueCriteria = criteriaList.find((u) => u.id === 2);
        if (valueCriteria && valueCriteria.types) {
          setValueObj(valueCriteria.types);
        } else {
          setValueObj(selected.types || []);
        }
        setIsTableSelected(true);
        setInputValue(false);
      } else if (selected && selected.id === 4) {
        setValueObj(selected.types);
        const testValueTypeId =
          testTypesList?.find((u) => u.valueType === selected.valueType) ?? null;
        setTestTypeValue(testValueTypeId);
        setInputValue(false);
        setIsTableSelected(false);
      } else {
        setValueObj(selected.types);
        setTestTypeValue('');
        setInputValue(true);
        setIsTableSelected(false);
      }
    }
  };

  /** Function for on select of unit value */
  const onChangeValue = (value) => {
    setTestValue(value);
    if (valueObj.length > 0) {
      const selected = valueObj.find((u) => u.value === value);
      const testValueTypeId =
        testTypesList && testTypesList.find((u) => u.valueType === selected.valueType);
      setTestTypeValue(testValueTypeId);
    }
  };

  const onChangeCompValue = (value) => {
    setComparator(value);
    if (value === '>' || value === '>=') {
      setMaxValue('0');
      testForm.setFieldsValue({ maxValue: '0' });
    } else if (value === '<' || value === '<=' || value === '=') {
      testForm.setFieldsValue({ minValue: '0' });
      setMinValue('0');
    }
  };

  // Handle row and column count input
  const handleRowColumnChange = () => {
    const rowNum = testForm.getFieldValue('rowCount') || 0;
    const colNum = testForm.getFieldValue('colCount') || 0;
    setRowCount(rowNum);
    setColCount(colNum);

    // Update state with new row/column structure matching the required format
    setDataTable(() => {
      return {
        rows: Array.from({ length: rowNum }, (_, i) => ({
          name: `Row ${i + 1}`,
          values: Array.from({ length: colNum }, () => ''), // Empty values for each row
          unit: '', // Table row unit - starts empty for user input
          comparator: '<', // Table row comparator - starts with default
          minValue: '', // Table row minValue - starts empty for user input
          maxValue: '', // Table row maxValue - starts empty for user input
        })),
        columns: Array.from({ length: colNum }, (_, i) => `Column ${i + 1}`),
      };
    });
  };

  // Update row name while preserving values
  const updateRowName = (index, newName) => {
    setDataTable((prevData) => {
      const updatedRows = [...prevData.rows];
      updatedRows[index] = { ...updatedRows[index], name: newName };
      return { ...prevData, rows: updatedRows };
    });
  };

  // Update column name
  const updateColumnName = (index, newName) => {
    setDataTable((prevData) => {
      const updatedColumns = [...prevData.columns];
      updatedColumns[index] = newName;
      return { ...prevData, columns: updatedColumns };
    });
  };

  // Update comparator for a specific row
  const updateRowComparator = (index, newComparator) => {
    setDataTable((prevData) => {
      const updatedRows = [...prevData.rows];
      updatedRows[index] = { ...updatedRows[index], comparator: newComparator };
      return { ...prevData, rows: updatedRows };
    });
  };

  // Update minValue for a specific row
  const updateRowMinValue = (index, newMinValue) => {
    setDataTable((prevData) => {
      const updatedRows = [...prevData.rows];
      updatedRows[index] = { ...updatedRows[index], minValue: newMinValue };
      return { ...prevData, rows: updatedRows };
    });
  };

  // Update maxValue for a specific row
  const updateRowMaxValue = (index, newMaxValue) => {
    setDataTable((prevData) => {
      const updatedRows = [...prevData.rows];
      updatedRows[index] = { ...updatedRows[index], maxValue: newMaxValue };
      return { ...prevData, rows: updatedRows };
    });
  };

  // Generate Table Columns
  const getTableColumns = () => {
    const tableColumns = [
      {
        title: <div className="row-title">Rows / Columns</div>,
        dataIndex: 'rowName',
        key: 'rowName',
        fixed: 'left',
      },
      ...dataTable.columns.map((col, index) => ({
        title: (
          <Input
            className="row-title"
            value={col}
            onChange={(e) => updateColumnName(index, e.target.value)}
            placeholder={`Column ${index + 1}`}
          />
        ),
        dataIndex: `col_${index}`,
        key: `col_${index}`,
        render: (_, row) => (
          <Input
            disabled
            value={row.values[index] || ''}
            placeholder="Enter value"
          />
        ),
      })),
      // Add Unit Selection Column (conditionally rendered - only for Multiple mode)
      ...(!showExtraFields ? [{
        title: <div className="row-title">Unit</div>,
        dataIndex: 'unit',
        key: 'unit',
        width: 80,
        render: (_, row) => (
          <Select
            style={{ width: '100%' }}
            placeholder="Unit"
            value={row.unit || undefined}
            dropdownClassName={MODAL_SELECT_DD}
            getPopupContainer={getPopupContainerBody}
            onChange={(value) => {
              setDataTable((prevData) => {
                const updatedRows = [...prevData.rows];
                updatedRows[row.key].unit = value;
                return { ...prevData, rows: updatedRows };
              });
            }}
          >
            {valueObj &&
              valueObj
                .filter(
                  (item) =>
                    item.value !== undefined &&
                    item.value !== '' &&
                    item.value !== 'Accept/Reject/NA' &&
                    item.value !== 'Text'
                )
                .map((item, i) => (
                  <Option key={i} value={item.value}>
                    {item.value}
                  </Option>
                ))}
          </Select>
        ),
      }] : []),
      // Add Comparator Column (conditionally rendered - only for Multiple mode)
      ...(!showExtraFields ? [{
        title: <div className="row-title">Comparator</div>,
        dataIndex: 'comparator',
        key: 'comparator',
        width: 80,
        render: (_, row) => (
          <Select
            style={{ width: '80%' }}
            placeholder="Comp"
            value={row.comparator || '<'}
            dropdownClassName={MODAL_SELECT_DD}
            getPopupContainer={getPopupContainerBody}
            onChange={(value) => updateRowComparator(row.key, value)}
          >
            {comparatorValue.map((item, i) => (
              <Option key={i} value={item}>
                {item}
              </Option>
            ))}
          </Select>
        ),
      }] : []),
      // Add Min Value Column (conditionally rendered - only for Multiple mode)
      ...(!showExtraFields && dataTable.rows.some(row =>
        row.comparator === '>' ||
        row.comparator === '>=' ||
        row.comparator === 'BETWEEN'
      ) ? [{
        title: <div className="row-title">Min</div>,
        dataIndex: 'minValue',
        key: 'minValue',
        width: 70,
        render: (_, row) => {
          const shouldShow = row.comparator === '>' ||
            row.comparator === '>=' ||
            row.comparator === 'BETWEEN';
          return shouldShow ? (
            <InputNumber
              className="pqt-modal-input-number"
              style={{ width: '100%' }}
              placeholder="Min"
              value={row.minValue}
              onChange={(value) => updateRowMinValue(row.key, value)}
            />
          ) : null;
        },
      }] : []),
      // Add Max Value Column (conditionally rendered - only for Multiple mode)
      ...(!showExtraFields && dataTable.rows.some(row =>
        row.comparator === '<' ||
        row.comparator === '<=' ||
        row.comparator === '=' ||
        row.comparator === 'BETWEEN'
      ) ? [{
        title: <div className="row-title">
          {dataTable.rows.some(row => row.comparator === '=') ? 'Value' : 'Max'}
        </div>,
        dataIndex: 'maxValue',
        key: 'maxValue',
        width: 70,
        render: (_, row) => {
          const shouldShow = row.comparator === '<' ||
            row.comparator === '<=' ||
            row.comparator === '=' ||
            row.comparator === 'BETWEEN';
          return shouldShow ? (
            <InputNumber
              className="pqt-modal-input-number"
              style={{ width: '100%' }}
              placeholder={row.comparator === '=' ? 'Value' : 'Max'}
              value={row.maxValue}
              onChange={(value) => updateRowMaxValue(row.key, value)}
              min={row.comparator === 'BETWEEN' ? (row.minValue || 0) : undefined}
            />
          ) : null;
        },
      }] : []),
    ];
    return tableColumns;
  };

  // Generate Table Data
  const getTableData = () => {
    const tableData = dataTable.rows.map((row, rowIndex) => ({
      key: rowIndex,
      rowName: (
        <Input
          value={row.name}
          onChange={(e) => updateRowName(rowIndex, e.target.value)}
          placeholder={`Row ${rowIndex + 1}`}
        />
      ),
      values: row.values,
      unit: row.unit, // Include unit data
      comparator: row.comparator, // Include comparator data
      minValue: row.minValue, // Include minValue data
      maxValue: row.maxValue, // Include maxValue data
    }));
    return tableData;
  };

  const showAddTestShell = isTestModalVisible && stationMatchesStoredSow();

  return (
    <>
      <CrceButton
        label="Add Test"
        variant="tertiary"
        size="small"
        icon={Plus}
        onClick={!isWriteAccess ? showModal : undefined}
        disabled={isWriteAccess ? true : undefined}
      />

      {showAddTestShell && (
        <div
          className="mc-dpr-modal-backdrop mc-dpr-modal-backdrop--add-test"
          role="presentation"
          onClick={() => !loadingBtn && handleCancel()}
        >
          <div
            className="mc-dpr-modal mc-dpr-modal--large add-new-test-mc-modal"
            role="dialog"
            aria-labelledby="add-new-test-modal-title"
            style={{ boxShadow: 'none', filter: 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mc-dpr-modal-head">
              <h3
                id="add-new-test-modal-title"
                className="mc-dpr-modal-title"
              >
                {editTest ? 'Edit Test' : 'Add Test'}
              </h3>
              <button
                type="button"
                className="mc-dpr-modal-close"
                aria-label="Close"
                disabled={loadingBtn}
                onClick={handleCancel}
              >
                <X size={20} aria-hidden />
              </button>
            </div>
            <div className="mc-dpr-modal-body">
              <Form form={testForm} name="basic" layout="vertical" autoComplete="off">
            {/* Additional Fields */}
            <Row gutter={16}>
              <Col lg={12}>
                <Form.Item label="Acceptance Criteria" name="acceptanceCriteria" required>
                  <TextArea
                    size="large"
                    style={{
                      height: 60,
                      overflowY: 'auto',
                      resize: 'none',
                    }}
                  />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item label="Frequency" name="frequency" required>
                  <TextArea
                    size="large"
                    style={{
                      height: 60,
                      overflowY: 'auto',
                      resize: 'none',
                    }}
                  />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item label="Applicable Standards & Codes" name="applicableStandardsCode" required>
                  <TextArea
                    size="large"
                    style={{
                      height: 60,
                      overflowY: 'auto',
                      resize: 'none',
                    }}
                  />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item label="Test Equipments" name="testEquipment" required>
                  <TextArea
                    size="large"
                    style={{
                      height: 60,
                      overflowY: 'auto',
                      resize: 'none',
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col lg={24}>
                {/* Hidden form field to store inspectionAuthority for form management */}
                <Form.Item name="inspectionAuthority" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Inspection Authority" required>
                  <Row gutter={16}>
                    {['CRCE', 'Contractor', 'Company'].map((section) => (
                      <Col lg={8} key={section}>
                        <div
                          style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: '4px',
                            padding: '16px',
                            marginBottom: '16px',
                          }}
                        >
                          <Text
                            strong
                            style={{
                              fontSize: '16px',
                              marginBottom: '12px',
                              display: 'block',
                              color: '#0f172a',
                            }}
                          >
                            {section}
                          </Text>
                          <Checkbox.Group
                            style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '4px' }}
                            value={Object.keys(inspectionAuthority[section]).filter(
                              (key) => inspectionAuthority[section][key]
                            )}
                            onChange={(checkedValues) => {
                              const updated = { ...inspectionAuthority };
                              updated[section] = {
                                Inspection: checkedValues.includes('Inspection'),
                                Monitor: checkedValues.includes('Monitor'),
                                Hold: checkedValues.includes('Hold'),
                                WitnessPoint: checkedValues.includes('WitnessPoint'),
                                ReviewPoint: checkedValues.includes('ReviewPoint'),
                              };
                              setInspectionAuthority(updated);
                              // Update form field as well
                              testForm.setFieldsValue({ inspectionAuthority: updated });
                            }}
                            options={[
                              { label: 'Inspection', value: 'Inspection' },
                              { label: 'Monitor', value: 'Monitor' },
                              { label: 'Hold', value: 'Hold' },
                              { label: 'Witness Point', value: 'WitnessPoint' },
                              { label: 'Review Point', value: 'ReviewPoint' },
                            ]}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col lg={6}>
                <Form.Item
                  label="Test ID"
                  name="testId"
                  required
                  rules={[
                    {
                      required: true,
                      message: 'Please enter Test ID',
                    },
                    {
                      max: 50,
                      message: 'Test ID length must be less than or equal to 50 characters',
                    },
                  ]}
                >
                  <Input value={testId} onChange={(e) => setTestId(e.target.value)} size="large" />
                </Form.Item>
              </Col>
              <Col lg={6}>
                <Form.Item label="Type of Test" name="typeOfTest" required>
                  <Input
                    value={typeOfTest}
                    onChange={(e) => setTypeOfTest(e.target.value)}
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col lg={6}>
                <Form.Item label="Criteria" name="criteria" required>
                  <Select
                    className="client-select"
                    placeholder="Select Criteria"
                    onChange={onChangeCriteria}
                    value={criteria}
                    dropdownClassName={MODAL_SELECT_DD}
                    getPopupContainer={getPopupContainerBody}
                  >
                    {criteriaList &&
                      criteriaList.map((item, i) => (
                        <Option key={i} value={item && item.value}>
                          {item && item.value}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              {isTableSelected && (
                <>
                  <Col span={3}>
                    <Form.Item label="No of Rows" name="rowCount">
                      <Input type="number" min={1} onChange={handleRowColumnChange} size="large" />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item label="No of Columns" name="colCount">
                      <Input type="number" min={1} onChange={handleRowColumnChange} size="large" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label="Unit / Comparator Mode:"
                      name="extraFieldsMode"
                      className="report-radio add-new-test-comparator-mode"
                    >
                      <Radio.Group
                        value={showExtraFields}
                        onChange={(e) => {
                          setShowExtraFields(e.target.value);
                          testForm.setFieldsValue({ extraFieldsMode: e.target.value });

                          // When switching to Multiple (per row) mode, clear global values
                          if (e.target.value === false) {
                            setComparator('<');
                            setTestValue('');
                            setMinValue('0');
                            setMaxValue('0');
                            setGlobalComparator('<');
                            // Clear form field values
                            testForm.setFieldsValue({
                              testValue: '',
                              comparator: '<',
                              minValue: '0',
                              maxValue: '0',
                            });
                          }
                        }}
                        className="add-new-test-comparator-radio-group"
                      >
                        <Radio value={true}>Single (per table)</Radio>
                        <Radio value={false}>Multiple (per row)</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </>
              )}
              {inputValue && !isTableSelected && (
                <Col lg={6}>
                  <Form.Item label="Number of sample" name="NoOfSample" required>
                    <Input
                      value={typeOfTest}
                      onChange={(e) => setTypeOfTest(e.target.value)}
                      size="large"
                      type="number"
                      min={1}
                    />
                  </Form.Item>
                </Col>
              )}
              {(inputValue || (isTableSelected && showExtraFields === true)) && (
                <>
                  <Col lg={6}>
                    <Form.Item label="Unit" name="testValue" required>
                      <Select
                        className="client-select"
                        placeholder="Select Unit"
                        onChange={onChangeValue}
                        value={testValue}
                        dropdownClassName={MODAL_SELECT_DD}
                        getPopupContainer={getPopupContainerBody}
                      >
                        {valueObj &&
                          valueObj
                            .filter(
                              (item) =>
                                item.value !== undefined &&
                                item.value !== '' &&
                                item.value !== 'Accept/Reject/NA'
                            )
                            .map((item, i) => (
                              <Option key={i} value={item.value}>
                                {item.value}
                              </Option>
                            ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col lg={6}>
                    <Form.Item label="Comparator" name="comparator" required>
                      <Select
                        className="client-select"
                        placeholder="Select Comparator"
                        onChange={onChangeCompValue}
                        value={comparator}
                        dropdownClassName={MODAL_SELECT_DD}
                        getPopupContainer={getPopupContainerBody}
                      >
                        {comparatorValue.map((item, i) => (
                          <Option key={i} value={item}>
                            {item}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  {(comparator == '>' || comparator == '>=' || comparator == 'BETWEEN') && (
                    <Col lg={6}>
                      <Form.Item label="Min. Value" name="minValue" required>
                        <InputNumber
                          className="pqt-modal-input-number"
                          value={minValue}
                          type="number"
                          onChange={(value) => setMinValue(value)}
                          size="large"
                          disabled={comparator == '<' || comparator == '<=' || comparator == '='}
                        />
                      </Form.Item>
                    </Col>
                  )}

                  {(comparator == '<' ||
                    comparator == '<=' ||
                    comparator == '=' ||
                    comparator == 'BETWEEN') && (
                    <Col lg={6}>
                      <Form.Item
                        label={comparator == '=' ? 'Value' : 'Max. Value'}
                        name="maxValue"
                        required
                      >
                        <InputNumber
                          className="pqt-modal-input-number"
                          value={maxValue}
                          type="number"
                          min={comparator == 'BETWEEN' ? parseInt(minValue) : undefined}
                          onChange={(value) => {
                            setMaxValue(value);
                          }}
                          size="large"
                          disabled={comparator == '>' || comparator == '>='}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </>
              )}
            </Row>
            {(() => {
              // console.log('inputValue.....', inputValue);
              // console.log('isTableSelected.....', isTableSelected);

              return null;
            })()}
            {isTableSelected && rowCount > 0 && colCount > 0 && (
              <Table
                columns={getTableColumns()}
                dataSource={getTableData()}
                bordered
                pagination={false}
                className="itp-container"
              />
            )}
            {/* </Form> */}
            <div className="pqt-modal-options-stack">
              <div className="pqt-modal-option-row">
                <Form.Item name="mandatory" required className="pqt-modal-form-option report-radio">
                  <Radio.Group
                    onChange={(e) => setMandatory(e.target.value)}
                    value={mandatory}
                  >
                    <Radio value={true}>Mandatory</Radio>
                    <Radio value={false}>Optional</Radio>
                  </Radio.Group>
                </Form.Item>
                <p className="pqt-modal-option-hint">
                  {mandatory
                    ? '* Test required for all the welds.'
                    : '* Test is not required for all the welds.'}
                </p>
              </div>
              <div className="pqt-modal-option-row">
                <Form.Item name="imagesRequired" className="pqt-modal-form-option report-checkbox">
                  <Checkbox
                    checked={imagesRequired}
                    onChange={(e) => setImagesRequired(e.target.checked)}
                  >
                    Images
                  </Checkbox>
                </Form.Item>
                <p className="pqt-modal-option-hint">
                  {imagesRequired
                    ? '* For this test, image is required.'
                    : '* For this test, image is not required.'}
                </p>
              </div>
              <div className="pqt-modal-option-row">
                <Form.Item name="stopwatchValueRequired" className="pqt-modal-form-option report-checkbox">
                  <Checkbox
                    checked={stopwatchValueRequired}
                    onChange={(e) => setStopwatchValueRequired(e.target.checked)}
                  >
                    StopWatch
                  </Checkbox>
                </Form.Item>
                <p className="pqt-modal-option-hint">
                  {stopwatchValueRequired
                    ? '* For this test, Stopwatch is required.'
                    : '* For this test, Stopwatch is not required.'}
                </p>
              </div>
            </div>
              </Form>
            </div>
            <div className="mc-dpr-modal-foot">
              <CrceButton
                label="Cancel"
                variant="tertiary"
                size="medium"
                onClick={handleCancel}
                disabled={loadingBtn}
              />
              <CrceButton
                label={
                  loadingBtn
                    ? editTest
                      ? 'Updating…'
                      : 'Adding…'
                    : editTest
                      ? 'Update'
                      : 'Add'
                }
                variant="primary"
                size="medium"
                onClick={handleOk}
                disabled={loadingBtn}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddNewTest;